<template>
  <div v-if="showFullDetails" class="full-details-wrapper">
    <div class="px-4 pt-4 pb-2">
      <div class="subtitle1" data-testid="tickets-page-findings-title">
        {{ $t("ticketDetails.findings") }}
      </div>
    </div>

    <div v-for="(finding, index) in findings" :key="index" class="finding-panel">
      <div class="finding-panel-header pl-4 pt-2 pb-2">
        <span class="subtitle2" data-testid="tickets-page-finding-file-name">
          {{ finding.fileName }}
        </span>
      </div>
      <div class="px-4 py-2">
        <div v-if="getFilePath(finding)" class="finding-row ml-8 mb-2">
          <span class="subtitle2 finding-label" data-testid="tickets-page-file-path-title">
            {{ $t("ticketDetails.filePath") }}:
          </span>
          <div class="finding-value">
            <ellipsified-copy-text
              :text="getFilePath(finding)"
              :copiable-text="finding.path"
              :show-tooltip="true"
              :persistent-copy-btn="true"
              copy-btn-size="24"
              data-testid="tickets-page-file-path"
            />
          </div>
        </div>
        <div v-if="finding.virusName" class="finding-row ml-8 mb-2">
          <span class="subtitle2 finding-label" data-testid="tickets-page-virus-name-title">
            {{ $t("ticketDetails.virusName") }}:
          </span>
          <span class="body2" data-testid="tickets-page-virus-name">
            {{ finding.virusName }}
          </span>
        </div>
        <div v-if="finding.mimeType" class="finding-row ml-8 mb-2">
          <span class="subtitle2 finding-label" data-testid="tickets-page-mime-type-title">
            {{ $t("ticketDetails.mimeType") }}:
          </span>
          <span class="body2" data-testid="tickets-page-mime-type">
            {{ finding.mimeType }}
          </span>
        </div>
        <div v-if="finding.fileHash" class="finding-row ml-8 mb-2">
          <span class="subtitle2 finding-label" data-testid="tickets-page-file-hash-title">
            {{ $t("ticketDetails.fileHash") }}:
          </span>
          <div class="finding-value">
            <ellipsified-copy-text
              :persistent-copy-btn="true"
              :text="finding.fileHash"
              :show-tooltip="true"
              copy-btn-size="24"
              data-testid="tickets-page-file-hash"
            />
          </div>
        </div>
        <div class="finding-row mb-2 ml-8">
          <span class="subtitle2 finding-label" data-testid="tickets-page-created-title">
            {{ $t("ticketDetails.created") }}:
          </span>
          <span class="body2" data-testid="tickets-page-created">
            {{ getFormattedDateTime(ticket.creationTime, "MMM, DD, h:mm a") }}
          </span>
        </div>
        <div class="finding-row mb-2 ml-8">
          <span class="subtitle2 finding-label" data-testid="tickets-page-modified-title">
            {{ $t("ticketDetails.lastModified") }}:
          </span>
          <span class="body2" data-testid="tickets-page-modified">
            {{ getFormattedDateTime(ticket.endTime, "MMM, DD, h:mm a") }}
          </span>
        </div>
        <div v-if="finding.owner" class="finding-row ml-8 mb-2">
          <span class="subtitle2 finding-label" data-testid="tickets-page-owned-by-title">
            {{ $t("ticketDetails.ownedBy") }}:
          </span>
          <span class="body2" data-testid="tickets-page-owned-by">
            {{ finding.owner }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { getFormattedDateTime } from "@/_helpers/date";
import { computed, defineComponent, type PropType } from "vue";
import type { MalwareMetaData, TicketDetails } from "@/_store/tickets/tickets.module";
import { TicketType } from "@/constants/tickets";
import EllipsifiedCopyText from "@/components/EllipsifiedCopyText.vue";

export default defineComponent({
  components: { EllipsifiedCopyText },
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const malwareMetaData = computed(() => props.ticket.sections?.malwareMetaData);

    const showFullDetails = computed(
      () => props.ticket.eventType === TicketType.MALWARE_IN_CLOUD_DRIVE && !!malwareMetaData.value
    );

    const findings = computed<MalwareMetaData[]>(() =>
      malwareMetaData.value ? [malwareMetaData.value] : []
    );

    const getFilePath = (finding: MalwareMetaData) =>
      finding.path ? finding.path.split("/").join(" › ") : "";

    return {
      showFullDetails,
      findings,
      getFilePath,
      getFormattedDateTime,
    };
  },
});
</script>

<style lang="scss" scoped>
.full-details-wrapper {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
}

.finding-row {
  display: flex;
  align-items: center;
}

.finding-label {
  width: 120px;
  min-width: 120px;
}

.finding-value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.finding-panel:not(:last-child) {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale));
}

.finding-panel-header {
  height: 56px;
  display: flex;
  align-items: center;
  background-color: rgb(var(--v-theme-indigo-faint));
}
</style>
